import {Component, Input, isDevMode, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import * as moment from 'moment';

import {AuthenticationService} from '@app/shared/services/authentication.service';
import {DealerProfile} from '@app/domain/model/dealer-profile';
import {DidNotPurchaseComponent} from './did-not-purchase-modal/did-not-purchase.component';
import {DidNotPurchaseService} from './did-not-purchase-modal/did-not-purchase.service';
import {PurchaseReceiptReportComponent} from './purchase-receipt-report/purchase-receipt-report.component';
import {PurchaseReceiptReportService} from './purchase-receipt-report/purchase-receipt-report.service';
import {PurchaseReport} from './purchase-report';
import {PurchaseReportCreationEditingComponent} from './purchase-report-creation-editing/purchase-report-creation-editing.component';
import {ReportedType} from './reported-type';
import {ReportingPeriod} from './reporting-period';
import {ReportsInPeriod} from './reports-in-period';
import {ReportStatus} from '@app/domain/model/report-status';
import {ReportType} from '@app/domain/model/report-type';
import {ReadReportingPeriodService} from './read-reporting-period.service';
import {UserAuthority} from '@app/domain/model/user-authority';
import {PaperReportComponent} from '@app/feature/admin/quick-add/paper-report/paper-report.component';
import {SetStatusService} from '@app/feature/dealer/report/set-status/set-status.service';
import {ApprovalStatus} from '@app/domain/model/approval-status';
import {PaperCreditComponent} from '@app/feature/admin/quick-add/paper-credit/paper-credit.component';
import { appSettings } from '@app/shared/app-settings';
import { Months } from '@app/domain/model/months';

@Component({
  selector: 'app-dealer-report-log',
  templateUrl: './dealer-report-log-view.component.html',
  styleUrls: ['./dealer-report-log-view.component.scss']
})
export class DealerReportLogViewComponent implements OnInit {

  // make the enums' values accessible from template
  public ApprovalStatus: typeof ApprovalStatus = ApprovalStatus;
  public ReportStatus: typeof ReportStatus = ReportStatus;
  public ReportType: typeof ReportType = ReportType;
  public ReportedType: typeof ReportedType = ReportedType;

  @Input()
  profile: DealerProfile;

  // Used to filter reporting periods visible on UI
  currentFilter;

  reportingPeriods: ReportingPeriod[];

  filteredReportingPeriods: ReportingPeriod[];

  // Number of week for reporting period selected by the user
  selectedWeeks: number[] = [];

  // Years selectable by user
  selectableYears: number[];

  // Actual year, selected by the user, by default current one.
  selectedYear: number;

  loading: number;

  showDeleteWarning: number;

  appSettings = appSettings;

  constructor(
    private route: ActivatedRoute,
    private periodsReader: ReadReportingPeriodService,
    private modalService: NgbModal,
    private didNotPurchaseService: DidNotPurchaseService,
    private authenticator: AuthenticationService,
    private readonly purchaseReceiptReportService: PurchaseReceiptReportService,
    private setStatusService: SetStatusService
  ) {
  }

  ngOnInit() {
    const currentYear = moment().year();

    // By default we will view only current reporting periods, ...
    this.currentFilter = ReportingPeriod.currentOnly;

    // ... for current year, ...
    this.selectedYear = currentYear;

    // ... and allow the user to go back for 9 preceding years.
    this.selectableYears = Array.from({ length: 10 }, (v, k) => currentYear - k);

    this.readReportingPeriods(this.selectedYear);
  }

  /**
   * Open the 'Add Paper Report' modal dialog.
   *
   * @param period (optional) period for which we are adding the report
   */
  addPaperReport(periodIndex?: number) {
    const modal = this.modalService.open(
      PaperReportComponent,
      { backdrop: 'static', centered: true, keyboard: false, size: 'lg', windowClass: 'paper-report' }
    );
    modal.componentInstance.dealer = this.profile;

    if (typeof periodIndex != 'undefined') {
      const selectedPeriod: ReportingPeriod = this.filteredReportingPeriods[periodIndex];
      modal.componentInstance.month = selectedPeriod.start.getMonth();
      modal.componentInstance.year = selectedPeriod.start.getFullYear();
    }

    modal.result.then((isCreated: boolean) => {
      if (!isCreated) {
        return;
      }
      this.readReportingPeriods(this.selectedYear);
      // this.selectedWeeks.forEach((i: number) => {
      //   const selectedPeriod: ReportingPeriod = this.filteredReportingPeriods[i];
      //   selectedPeriod.purchaseReports = [];
      // });
      // this.selectedWeeks = [];
    });
  }

  /**
   * Open the 'Add Paper Credit' modal dialog.
   *
   * @param period (optional) period for which we are adding the report
   */
  addPaperCredit(periodIndex?: number) {
    const modal = this.modalService.open(
      PaperCreditComponent,
      { backdrop: 'static', centered: true, keyboard: false, size: 'lg', windowClass: 'paper-credit' }
    );
    modal.componentInstance.dealer = this.profile;
    if (typeof periodIndex != 'undefined') {
      const selectedPeriod: ReportingPeriod = this.filteredReportingPeriods[periodIndex];
      modal.componentInstance.period = selectedPeriod;
    }

    modal.result.then((isCreated: boolean) => {
      if (!isCreated) {
        return;
      }
      this.readReportingPeriods(this.selectedYear);
      // this.selectedWeeks.forEach((i: number) => {
      //   const selectedPeriod: ReportingPeriod = this.filteredReportingPeriods[periodIndex];
      //   if(selectedPeriod && selectedPeriod.purchaseReports){
      //     selectedPeriod.purchaseReports = [];
      //   }
      // });
      // this.selectedWeeks = [];
    });
  }

  /**
   * Open the 'Add Purchase Report' modal dialog.
   *
   * @param period (optional) period for which we are adding the report
   */
  addPurchase(periodIndex?: number) {
    window.scrollTo(0,0);
    const modal = this.modalService.open(
      PurchaseReportCreationEditingComponent,
      { backdrop: 'static', keyboard: false, size: 'lg', windowClass: 'purchase-report' }
    );
    if (typeof periodIndex != 'undefined') {
      const selectedPeriod: ReportingPeriod = this.filteredReportingPeriods[periodIndex];
      modal.componentInstance.period = selectedPeriod;
    }

    modal.componentInstance.profile = this.profile;
    modal.componentInstance.selectedYear = this.selectedYear;
    modal.componentInstance.marketTypes = this.profile.marketTypes;
    modal.result.then((isCreated: boolean) => {
      if (!isCreated) {
        return;
      }
      this.readReportingPeriods(this.selectedYear);
      // if(typeof periodIndex != 'undefined'){
      //   this.updateWeeklyReportList(periodIndex);
      // }
    });
  }

  /**
   * @returns true if upcoming reports are visible, false otherwise
   */
  areUpcomingVisible() {
    return !this.currentFilter;
  }

  /**
   * Opens the 'Did Not Purchase' modal dialog for the selected reporting period.
   *
   * @param period selected reporting period
   */
  didNotPurchase(periodIndex: number) {
    const selectedPeriod: ReportingPeriod = this.filteredReportingPeriods[periodIndex];
    const modal = this.modalService.open(
      DidNotPurchaseComponent,
      { backdrop: 'static', centered: true, keyboard: false, size: 'lg', windowClass: 'dnp-report' }
    );
    modal.componentInstance.period = selectedPeriod;
    modal.result.then(
      (result) => {
        if (!result.didNotPurchase) {
          return;
        }
        this.didNotPurchaseSubmit(result.startDate, selectedPeriod);
      }, () => {});
  }

  didNotPurchaseWithoutWarning(periodIndex: number) {
    const selectedPeriod: ReportingPeriod = this.filteredReportingPeriods[periodIndex];
    this.didNotPurchaseSubmit(selectedPeriod.start, selectedPeriod);
  }

  private didNotPurchaseSubmit(resultStartDate: Date, selectedPeriod: ReportingPeriod) {
    this.didNotPurchaseService.CreateDidNotPurchaseReport(resultStartDate, this.getDealerId()).subscribe((reportingPeriod: ReportingPeriod) => {
      this.updateSelectedReportingPeriod(reportingPeriod);
    }, (error: Error) => {
      this.periodsReader.getReportsByWeek(selectedPeriod.start, this.getDealerId()).subscribe((reportsInPeriod: ReportsInPeriod) => {
        this.updateSelectedReportingPeriod(reportsInPeriod.period);
      });
      throw error;
    });
  }

  private getDealerId() {
    const executor = this.authenticator.getCurrentUser();
    return executor.hasAuthority(UserAuthority.DEALER_INFO) ? this.profile.id : null;
  }

  hasManageReportAuthority() {
    const executor = this.authenticator.getCurrentUser();
    return executor.hasAuthority(UserAuthority.MANAGE_REPORT);
  }

  /**
   * Opens reporting period details.
   *
   * @param reportingPeriod Information about the reporting period.
   */
  openPeriod(index: number) {
    const selectedPeriod: ReportingPeriod = this.filteredReportingPeriods[index];
    if (this.selectedWeeks && this.selectedWeeks.includes(index)) {
      this.selectedWeeks.splice(this.selectedWeeks.indexOf(index),1);

      return;
    }

    if(!selectedPeriod.purchaseReports) {
      this.loading = index;
      this.periodsReader
        .getReportsByWeek(selectedPeriod.start, this.getDealerId())
        .subscribe((reportsInPeriod: ReportsInPeriod) => {
          selectedPeriod.purchaseReports = reportsInPeriod.reports;
          // this.purchaseReports = reportsInPeriod.reports;
          this.loading = null;
        });
    }
    this.selectedWeeks.push(index);
  }

  selectYear(year) {
    this.selectedWeeks = [];
    this.readReportingPeriods(year);
  }

  /**
   * Toggles visibility of upcoming reports.
   */
  toggleUpcoming() {
    if (this.areUpcomingVisible()) {
      this.currentFilter = ReportingPeriod.currentOnly;
      this.filteredReportingPeriods = this.reportingPeriods.filter(this.currentFilter);
    } else {
      this.currentFilter = null;
      this.filteredReportingPeriods = this.reportingPeriods;
    }
  }

  openPurchaseReceiptReport(reportId: number, submittedBy: string, periodIndex: number) {
    const selectedPeriod: ReportingPeriod = this.filteredReportingPeriods[periodIndex];

    const modal = this.modalService.open(
      PurchaseReceiptReportComponent,
      { backdrop: 'static', keyboard: false, size: 'lg', windowClass: 'purchase-report' }
    );

    modal.componentInstance.reportId = reportId;
    modal.componentInstance.period = selectedPeriod;
    modal.componentInstance.submittedBy = submittedBy;
    modal.componentInstance.marketTypes = this.profile.marketTypes;
    modal.componentInstance.profile = this.profile;

    modal.result.then((isUpdated: boolean) => {
      if (isUpdated) {
        this.updateWeeklyReportList(periodIndex);
      }
      this.showDeleteWarning = null;
    });
  }

  deleteReceiptReport(reportId: number, periodIndex: number) {
    if (this.showDeleteWarning !== reportId) {
      this.showDeleteWarning = reportId;

      return;
    }

    this.purchaseReceiptReportService.deletePurchaseReceiptReport(reportId).subscribe(() => {
      this.showDeleteWarning = null;
      this.updateWeeklyReportList(periodIndex);
    });

  }

  private readReportingPeriods(selectedYear) {
    // const reportingPeriodsClone: ReportingPeriod[] = this.filteredReportingPeriods ? this.filteredReportingPeriods.slice() : [];
    this.periodsReader
      .readReportingPeriods(selectedYear, this.getDealerId())
      .subscribe((reportingPeriods: ReportingPeriod[]) => {
        this.reportingPeriods = reportingPeriods;

        if (this.currentFilter) {
          this.filteredReportingPeriods = reportingPeriods.filter(this.currentFilter);
        } else {
          this.filteredReportingPeriods = reportingPeriods;
        }
        this.selectedWeeks.forEach((i: number) => {
          this.updateWeeklyReportList(i);
          // const selectedPeriod: ReportingPeriod = this.filteredReportingPeriods[i];
          // if(reportingPeriodsClone[i]){
          //   selectedPeriod.purchaseReports = reportingPeriodsClone[i].purchaseReports;
          // }
        });

      });
  }

  private updateWeeklyReportList(periodIndex: number) {
    const selectedPeriod: ReportingPeriod = this.filteredReportingPeriods[periodIndex];

    if (!selectedPeriod) {

      return;
    }

    this.loading = periodIndex;

    this.periodsReader
      .getReportsByWeek(selectedPeriod.start, this.getDealerId())
      .subscribe((reportsInPeriod: ReportsInPeriod) => {
        this.updateSelectedReportingPeriod(reportsInPeriod.period);
        selectedPeriod.purchaseReports = reportsInPeriod.reports;
        this.loading = null;
      });
  }

  private updateSelectedReportingPeriod(selectedPeriod: ReportingPeriod) {
    const selectedPeriodIndex = this.reportingPeriods.findIndex(item => item.weekOfYear === selectedPeriod.weekOfYear);
    Object.assign(this.reportingPeriods[selectedPeriodIndex], selectedPeriod);
  }

  private onStatusChange(reportId: number, status: ApprovalStatus) {
    this.setStatusService.setStatus(reportId, status).subscribe((data) => {
      if (isDevMode()) {
        console.log(data);
      }
    });
  }

}
